import {useCallback, useEffect, useReducer, useRef} from 'react';

import {LONG_SNOOZE_DAYS, MIN_QUIZ_QUESTIONS} from '../constants';
import {RecommendedAssessmentEventPayload} from '../events';
import {PopQuizEligibility} from '../pop-quiz-eligibility';
import {
    isDebugQuizSuppressed,
    publishAlternateQuizSelected,
    publishContinueCourseSelected,
    publishDismissed,
    publishFeedbackSubmitted,
    publishPresented,
    publishQuizStarted,
    publishRecommendedAssessmentLinkSelected,
    publishRecommendedAssessmentPresented,
    publishSnoozed,
} from '../pop-quiz-tracking';
import {Phase, PopQuizFeedbackRating, PopQuizItem, QuizCandidate} from '../types';
import {initialPopQuizLifecycleState, popQuizLifecycleReducer} from './pop-quiz-lifecycle-reducer';

export interface UsePopQuizLifecycleArgs {
    quizData: QuizCandidate | undefined;
    supportedItems: PopQuizItem[];
    isItemSearchFetching: boolean;
    isAssessmentsCatalogSettled: boolean;
    areOtherModalsOpen: boolean;
    assessmentEventPayload: RecommendedAssessmentEventPayload | undefined;
    requestDifferentQuiz: () => void;
    eligibility: PopQuizEligibility;
    startAttempt: (quizId: string) => Promise<string | undefined>;
}

export interface UsePopQuizLifecycleResult {
    isOpen: boolean;
    phase: Phase;
    questions: PopQuizItem[];
    correctCount: number;
    attemptId: string | undefined;
    stopShowing: boolean;
    setStopShowing: (stopShowing: boolean) => void;
    onAccept: () => Promise<void>;
    onComplete: (finalCorrectCount: number) => void;
    onPromptDismiss: () => void;
    onRequestDifferentQuiz: () => void;
    onSnoozingExit: () => void;
    onSnoozedClose: () => void;
    onFeedback: (rating: PopQuizFeedbackRating) => void;
    onRecommendedAssessmentSelected: () => void;
    onContinueLearning: () => void;
}

export function usePopQuizLifecycle({
    quizData,
    supportedItems,
    isItemSearchFetching,
    isAssessmentsCatalogSettled,
    areOtherModalsOpen,
    assessmentEventPayload,
    requestDifferentQuiz,
    eligibility,
    startAttempt,
}: UsePopQuizLifecycleArgs): UsePopQuizLifecycleResult {
    const [state, dispatch] = useReducer(popQuizLifecycleReducer, initialPopQuizLifecycleState);

    const hasShownRef = useRef(false);
    // Guards against duplicate snooze events if multiple dismiss paths fire before a re-render.
    const isSnoozingRef = useRef(false);

    // Show prompt once when quiz data is ready, items are loaded, the assessments catalog fetch
    // has settled and no other modals are open. (`quizData &&` is kept out of `showPrompt` itself
    // so its truthiness still narrows `quizData` below, rather than just gating a boolean.)
    useEffect(() => {
        const showPrompt =
            !areOtherModalsOpen &&
            !hasShownRef.current &&
            !isItemSearchFetching &&
            isAssessmentsCatalogSettled &&
            supportedItems.length >= MIN_QUIZ_QUESTIONS;

        if (!quizData || !showPrompt) {
            return;
        }

        hasShownRef.current = true;
        publishPresented(quizData, supportedItems.length);
        if (assessmentEventPayload) {
            publishRecommendedAssessmentPresented(
                quizData,
                supportedItems.length,
                assessmentEventPayload,
            );
        }
        dispatch({type: 'PRESENTED'});
    }, [
        quizData,
        areOtherModalsOpen,
        isItemSearchFetching,
        isAssessmentsCatalogSettled,
        supportedItems,
        assessmentEventPayload,
    ]);

    const onAccept = useCallback(async () => {
        if (!quizData || supportedItems.length === 0) {
            return;
        }

        // Start a server-side attempt record. Suppressed for debug quizzes in prod environment.
        let newAttemptId: string | undefined;
        if (!isDebugQuizSuppressed(quizData)) {
            try {
                newAttemptId = await startAttempt(String(quizData.quizId));
                // `?? 0` preserves the pre-existing "no attempt id" sentinel sent to analytics —
                // Number(undefined) is NaN, which would corrupt the Avro `long` field downstream.
                publishQuizStarted(quizData, supportedItems.length, Number(newAttemptId ?? 0));
            } catch {
                // Proceed without an attemptId — per-answer submissions will also be skipped.
            }
        }

        eligibility.markQuizOffered(quizData.quizId);
        dispatch({type: 'ACCEPTED', attemptId: newAttemptId, questions: supportedItems});
    }, [quizData, supportedItems, startAttempt, eligibility]);

    const onComplete = useCallback((finalCorrectCount: number) => {
        dispatch({type: 'COMPLETED', correctCount: finalCorrectCount});
    }, []);

    const onClose = useCallback(() => {
        isSnoozingRef.current = false;
        dispatch({type: 'CLOSED'});
        if (quizData) {
            eligibility.markQuizDismissed(quizData.quizId);
        }
    }, [quizData, eligibility]);

    // Shared by onPromptDismiss and onSnoozingExit
    const snoozePopQuiz = useCallback(
        (currentQuizData: QuizCandidate, numberOfQuestions: number) => {
            isSnoozingRef.current = true;
            eligibility.markSnoozed();
            publishSnoozed(currentQuizData, numberOfQuestions, LONG_SNOOZE_DAYS);
            dispatch({type: 'SNOOZED'});
        },
        [eligibility],
    );

    // Snoozes for 60 days if the checkbox is checked; otherwise fires a dismissed event and closes.
    const onPromptDismiss = useCallback(() => {
        if (state.stopShowing && quizData && !isSnoozingRef.current) {
            snoozePopQuiz(quizData, supportedItems.length);
        } else {
            publishDismissed(quizData, supportedItems.length);
            onClose();
        }
    }, [state.stopShowing, quizData, supportedItems, snoozePopQuiz, onClose]);

    const onRequestDifferentQuiz = useCallback(() => {
        if (!quizData) {
            return;
        }
        publishAlternateQuizSelected(quizData, supportedItems.length);
        requestDifferentQuiz();
    }, [quizData, supportedItems.length, requestDifferentQuiz]);

    // Snoozes for 60 days if the checkbox is checked; otherwise closes normally (3-day cap).
    const onSnoozingExit = useCallback(() => {
        if (state.stopShowing && quizData && !isSnoozingRef.current) {
            snoozePopQuiz(quizData, state.questions.length);
        } else {
            onClose();
        }
    }, [state.stopShowing, quizData, state.questions, snoozePopQuiz, onClose]);

    const onSnoozedClose = useCallback(() => {
        if (quizData) {
            eligibility.markQuizDismissed(quizData.quizId);
        }
        dispatch({type: 'SNOOZED_MODAL_CLOSED'});
    }, [quizData, eligibility]);

    const onFeedback = useCallback(
        (rating: PopQuizFeedbackRating) => {
            if (quizData) {
                publishFeedbackSubmitted(quizData, state.questions.length, rating);
            }
        },
        [quizData, state.questions.length],
    );

    const onRecommendedAssessmentSelected = useCallback(() => {
        if (quizData && assessmentEventPayload) {
            publishRecommendedAssessmentLinkSelected(
                quizData,
                supportedItems.length,
                assessmentEventPayload,
            );
        }
    }, [quizData, assessmentEventPayload, supportedItems]);

    const onContinueLearning = useCallback(() => {
        if (quizData && typeof window !== 'undefined') {
            publishContinueCourseSelected(quizData, state.questions.length);
            eligibility.markShownToday();
            window.location.assign(quizData.courseUrl);
        }
    }, [quizData, state.questions.length, eligibility]);

    const setStopShowing = useCallback((stopShowing: boolean) => {
        dispatch({type: 'STOP_SHOWING_CHANGED', stopShowing});
    }, []);

    return {
        isOpen: state.isOpen,
        phase: state.phase,
        questions: state.questions,
        correctCount: state.correctCount,
        attemptId: state.attemptId,
        stopShowing: state.stopShowing,
        setStopShowing,
        onAccept,
        onComplete,
        onPromptDismiss,
        onRequestDifferentQuiz,
        onSnoozingExit,
        onSnoozedClose,
        onFeedback,
        onRecommendedAssessmentSelected,
        onContinueLearning,
    };
}
